import { useShellContext } from '@omni/kit';
import HealthMonitor from '@omni/kit/HealthMonitor';
import {
  KitBadge,
  KitListItem,
  KitLoader,
  KitText,
} from '@omni/kit/components';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitList from '@omni/kit/components/KitList';
import KitModal from '@omni/kit/components/KitModalV2/KitModalV2';
import KitNotFound from '@omni/kit/components/KitNotFound';
import {
  KitSnackDuration,
  KitSnackRender,
} from '@omni/kit/components/KitSnack';
import KitTab from '@omni/kit/components/KitTab';
import {
  SizeClass,
  SizeClassV2,
  WindowSizeMin,
  useSizeClass,
} from '@omni/kit/contexts/SizeClassContext';
import { useFeatureFlag } from '@omni/kit/hooks/useFeatureFlag';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import {
  IAttendance,
  IGroupAttendances,
  IGroupEvent,
  IGroupHeadCounts,
  IGroupLocation,
} from '@omni/kit/services/GroupsService/Types';
import { UNLEASH_TOGGLES } from '@omni/kit/services/UnleashService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Keyboard, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { ChannelMember } from '../../../Types';
import { SendbirdUserMessage } from '../../../Types';
import { useChannelGroup } from '../../../hooks/useChannelGroup';
import useGetMessageIdForEventId from '../../../hooks/useGetMessageIdForEventId';
import { sortMembers } from '../../../utilities/sortGroupMembers';
import * as Constants from '../../Constants';
import { sendEventMessage } from '../../redux/actions/ChatActions';
import {
  accessTokenSelector,
  channelSelector,
  memberListSelector,
  ownerListSelector,
  userSelector,
} from '../../redux/selectors';
import CreateEditGroupEventScreen from '../channelDetails/CreateEditGroupEventScreen';
import CreateOrUpdateGroupAttendance from './components/CreateOrUpdateGroupAttendance';
import DeleteEventConfirmModal from './components/DeleteEventConfirmModal';
import EventAttendanceBarComponent from './components/EventAttendanceBarComponent';
import EventAttendanceComponent from './components/EventAttendanceComponent';
import EventDetailHeader from './components/EventDetailHeader';
import EventDetailLocation from './components/EventDetailLocation';
import MoreActionModal from './components/MoreActionModal';
import ShareEventConfirmModal from './components/ShareEventConfirmModal';

const debug = require('debug')('tca:chat:screen:EventDetailsScreen');
type EventServiceFunction = (params: {
  body?: any;
  token?: string;
  id: string;
}) => Promise<any>;

export interface RouteProps extends RouteProp<ParamListBase, string> {
  params: {
    eventId: any;
    activeMessage: SendbirdUserMessage;
    isRepeatingEvent: boolean;
  };
}

export default function EventDetailsScreen(): JSX.Element {
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }
  const { t } = useTranslation(['groups']);
  // Routes
  let route: RouteProps;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    route = useRoute<RouteProps>();
  } catch {
    route = {} as RouteProps;
  }
  const dispatch = useDispatch();
  const { sizeClassV2, windowHeight, windowWidth } = useSizeClass();
  const { app } = useShellContext();
  const appKey = app.appKey;
  const accessToken = useSelector(accessTokenSelector);
  const [isLoading, setIsLoading] = useState(false);
  // @ts-ignore
  const { eventId, activeMessage, isRepeatingEvent } = route.params;
  const [activeTab, setActiveTab] = useState(0);
  const isLarge = sizeClassV2 >= SizeClassV2.L;
  const isLandscape = windowHeight < windowWidth;

  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
  });

  const [groupEvent, setGroupEvent] = useState<IGroupEvent | undefined>();
  const [repeatingGroupEvent, setRepeatingGroupEvent] = useState<
    IGroupEvent | undefined
  >();
  const [moreActionModal, setMoreActionModal] = useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<boolean>(false);
  const [eventToShare, setEventToShare] = useState<boolean>(false);
  const [headcounts, setHeadcounts] = useState<IGroupHeadCounts | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoadingHeadCounts, setIsLoadingHeadCounts] = useState(true);
  const [groupEventAttendances, setGroupEventAttendances] =
    useState<IGroupAttendances | null>(null);
  const [isResponseAttendanceError, setIsResponseAttendanceError] =
    useState<boolean>(false);
  const [isLoadingGroupEventAttendances, setIsLoadingGroupEventAttendances] =
    useState<boolean>(true);
  const [eventLocation, setEventLocation] = useState<
    IGroupLocation | undefined
  >();
  const [selectedDeleteOption, setSelectedDeleteOption] = useState<
    string | undefined
  >(undefined);
  const [isResponseError, setIsResponseError] = useState<boolean>(false);
  const channel = useSelector(channelSelector) as GroupChannel;
  const group = useChannelGroup(channel?.url);
  const [openEditGroupEventModal, setIsOpenEditGroupEventModal] =
    useState<boolean>(false);
  const isManager = channel?.myRole === 'operator';
  let isFocused: boolean;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isFocused = useIsFocused();
  } catch {
    isFocused = true; // Default to focused for web environments without navigation context
  }

  const memberList = useSelector(memberListSelector);
  const ownerList = useSelector(ownerListSelector);
  const currentUser = useSelector(userSelector);
  const currentId = currentUser?.userId;
  const isManagerMember = (member: ChannelMember): boolean => {
    return ownerList.some((m) => m.userId === member.userId);
  };

  const { sizeClass } = useSizeClass();
  const isSmall = sizeClass === SizeClass.Small;
  const [repeatEvent, setRepeatEvent] = useState<IGroupEvent | undefined>();
  // Messages to be deleted also in the chat feed if the event is deleted
  const groupEventChatList = useGetMessageIdForEventId(
    repeatingGroupEvent?.id ? repeatingGroupEvent?.id : groupEvent?.id ?? ''
  );
  const repeatingEventInstance = groupEvent?._embedded?.['repeating-event'];
  const repeatingEvent = groupEvent?.repetition_rules;
  const [newGroupEvent, setNewGroupEvent] = useState<IGroupEvent | undefined>();

  // Feature flag for showing the group attendance
  const { isEnabled: isGroupAttendanceEnabled, isReady } = useFeatureFlag(
    UNLEASH_TOGGLES.DISPATCH_GROUP_ATTENDANCE
  );
  const groupMembersLessThanFifty = memberList && memberList?.length < 50;
  const isEventPast = groupEvent?.start_at
    ? moment().isAfter(groupEvent.start_at)
    : false;
  const showTakeAttendanceComponent = useMemo(() => {
    return isManager && isEventPast && isGroupAttendanceEnabled;
  }, [isManager, isEventPast, isGroupAttendanceEnabled]);

  const isAttendanceTaken = useMemo(() => {
    const headcountValue = headcounts?.count || 0;

    return headcountValue > 0;
  }, [headcounts?.count]);

  const [isAttendanceModalVisible, setAttendanceModalVisible] = useState(false);
  const [attendanceMode, setAttendanceMode] = React.useState<'create' | 'edit'>(
    'create'
  );
  const sortedMembers = useMemo(() => sortMembers(memberList), [memberList]);
  const attendances = useMemo(() => {
    return groupEventAttendances?._embedded?.attendances ?? [];
  }, [groupEventAttendances]);

  const { attendedMembers, notAttendedMembers } = useMemo(() => {
    const attended: typeof sortedMembers = [];
    const notAttended: typeof sortedMembers = [];

    sortedMembers.forEach((member) => {
      const endUserId =
        member._embedded?.['chat-user']?._embedded?.['end-user']?.id;

      const attendance = attendances.find(
        (a) => a._embedded?.['end-user']?.id === endUserId
      );

      if (attendance?.attended) {
        attended.push(member);
      } else {
        notAttended.push(member);
      }
    });

    return { attendedMembers: attended, notAttendedMembers: notAttended };
  }, [sortedMembers, attendances]);

  //****************************************************************************
  // Methods
  //****************************************************************************

  const showKitSnack = (msg: string, dur: number) => {
    setSnackOptions({ visible: true, message: msg, duration: dur });
  };

  const openMoreActionModal = () => {
    setMoreActionModal(true);
  };

  const closeMoreActionModal = () => {
    setMoreActionModal(false);
  };
  const groupEventRef = React.useRef(groupEvent);

  const handleAttendancePress = (mode: 'create' | 'edit') => {
    if (Platform.OS === 'web') {
      setAttendanceMode(mode);
      setAttendanceModalVisible(true);
    } else {
      // Navigate to another screen on mobile
      navigation.navigate(Constants.CREATE_EDIT_GROUP_ATTENDANCE_SCREEN, {
        title:
          mode === 'edit'
            ? t('groups:editAttendance')
            : t('groups:takeAttendanceButton'),
        mode,
        headcounts,
        groupEventAttendances,
        groupEvent,
        onSuccess: () => {
          fetchGroupEventAttendances();
          fetchGroupEventHeadcounts();
        },
      });
    }
  };

  useEffect(() => {
    groupEventRef.current = groupEvent; // Keep the latest value in the ref
  }, [groupEvent]);

  const fetchRepeatGroupEvent = useCallback(() => {
    const _fetchRepeatGroupEvent = async (id: string) => {
      try {
        const response = await GroupsService.GetRepeatingGroupEvent({
          id,
          token: accessToken,
        });

        if (response.body) {
          setRepeatEvent(response.body);
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      }
    };

    if (repeatingEventInstance) {
      _fetchRepeatGroupEvent(repeatingEventInstance.id);
    }
  }, [accessToken, repeatingEventInstance]);

  // Hooks
  useEffect(() => {
    fetchRepeatGroupEvent();
  }, [fetchRepeatGroupEvent]);

  const _onEditEvent = useCallback(() => {
    setMoreActionModal(false);
    if (Platform.OS === 'web' && !isSmall) {
      setIsOpenEditGroupEventModal(true);
    } else {
      setTimeout(() => {
        Keyboard.dismiss();
        navigation.navigate('channel_details_modal', {
          screen: 'create_event',
          params: {
            event: repeatingGroupEvent ? repeatingGroupEvent : groupEvent,
            activeMessage: activeMessage,
            setNewGroupEvent: setNewGroupEvent,
          },
        });
      }, 500);
    }
  }, [groupEvent, isSmall, navigation, activeMessage, repeatingGroupEvent]);

  const _onDeleteEvent = useCallback(() => {
    setMoreActionModal(false);

    setTimeout(() => {
      setEventToDelete(true);
    }, 500);
  }, []);

  const _onShareEvent = () => {
    setMoreActionModal(false);
    setTimeout(() => {
      setEventToShare(true);
    }, 500);
  };

  const _confirmDeleteEvent = useCallback(async () => {
    const deleteGroupEventService =
      (repeatEvent || repeatingEvent) && selectedDeleteOption === 'all'
        ? GroupsService.DeleteRepeatingGroupEvent
        : GroupsService.DeleteGroupEvent;
    try {
      const response = await deleteGroupEventService({
        id:
          selectedDeleteOption === 'all'
            ? repeatEvent?.id || groupEvent?.id
            : selectedDeleteOption === 'only' && repeatingEvent
            ? groupEvent?._embedded?.event?.[0].id
            : eventId,
        token: accessToken,
      });

      if (response.status === 204) {
        if (Platform.OS !== 'web') {
          setEventToDelete(false);
        }

        if (groupEventChatList && groupEventChatList.length > 0) {
          groupEventChatList.forEach((message) => {
            channel.deleteMessage(message);
          });
        }

        navigation.goBack();

        showKitSnack(t('groups:eventDeleted'), KitSnackDuration.SHORT);
      } else {
        if (response.status && !excludedStatusCodes.includes(response.status)) {
          HealthMonitor.logError(response.message);
        }
      }
    } catch (error) {
      HealthMonitor.logError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    eventId,
    groupEventChatList,
    repeatEvent,
    selectedDeleteOption,
  ]);

  const _confirmShareEvent = useCallback(async () => {
    setEventToShare(false);
    const sendEvent = (event: IGroupEvent | undefined) => {
      if (!event) {
        return;
      }

      dispatch(
        sendEventMessage(
          channel as GroupChannel,
          {
            groupEvent: event,
          },
          t('groups:eventShared')
        )
      );
    };

    setTimeout(() => {
      sendEvent(groupEvent);
      showKitSnack(t('groups:eventSharedToChat'), KitSnackDuration.LONG);
    }, 100);
  }, [channel, dispatch, groupEvent, t]);

  //****************************************************************************
  // Lifecycle
  //****************************************************************************
  const getUpdateGroupEventService = useCallback(() => {
    if (
      (newGroupEvent !== undefined &&
        newGroupEvent.repetition_rules !== undefined) ||
      (eventId && isRepeatingEvent)
    ) {
      return { getEventService: GroupsService.GetRepeatingGroupEvent };
    } else if (
      (newGroupEvent !== undefined &&
        newGroupEvent.repetition_rules === undefined) ||
      (eventId && !isRepeatingEvent)
    ) {
      return { getEventService: GroupsService.GetGroupEvent };
    } else if (newGroupEvent === undefined || (eventId && !isRepeatingEvent)) {
      return { getEventService: GroupsService.GetGroupEvent };
    }

    return { getEventService: GroupsService.GetGroupEvent };
  }, [isRepeatingEvent, eventId, newGroupEvent]);

  const fetchGroupEvent = useCallback(() => {
    const _fetchGroupEvent = async (id: string) => {
      const { getEventService } = getUpdateGroupEventService() as {
        getEventService?: EventServiceFunction;
      };

      setIsLoading(true);
      try {
        if (getEventService) {
          const response = await getEventService({
            id: newGroupEvent?.id ?? eventId,
            token: accessToken,
          });

          if (response.body) {
            setGroupEvent(response.body);
          } else {
            setIsResponseError(true);
            if (
              response.status &&
              !excludedStatusCodes.includes(response.status)
            ) {
              HealthMonitor.logError(response.message);
            }
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoading(false);
      }
    };

    _fetchGroupEvent(newGroupEvent?.id ?? eventId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, navigation, eventId, newGroupEvent, setNewGroupEvent]);

  // Hooks
  useEffect(() => {
    fetchGroupEvent();
  }, [fetchGroupEvent, newGroupEvent, isFocused]);

  // Fetching repeating group event data if exists
  useEffect(() => {
    const _fetchRepeatingGroupEvent = async (id: string) => {
      setIsLoading(true);
      try {
        const response = await GroupsService.GetRepeatingGroupEvent({
          id,
          token: accessToken,
        });

        if (response.body) {
          setRepeatingGroupEvent(response.body);
        } else {
          setIsResponseError(true);
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (groupEvent?._embedded?.['repeating-event']) {
      _fetchRepeatingGroupEvent(groupEvent?._embedded?.['repeating-event'].id);
    }
  }, [groupEvent, accessToken]);

  // Hooks
  useEffect(() => {
    if (groupEvent?._embedded?.location !== null) {
      setEventLocation(groupEvent?._embedded?.location as IGroupLocation);
    } else {
      setEventLocation(undefined);
    }
  }, [groupEvent?._embedded?.location, isFocused]);

  useEffect(() => {
    navigation.setParams({
      openMoreActionModal,
      isManager,
      eventExists: groupEvent !== undefined,
      onEditEvent: _onEditEvent,
      onDeleteEvent: _onDeleteEvent,
      onShareEvent: _onShareEvent,
    });
  }, [navigation, isManager, groupEvent, _onEditEvent, _onDeleteEvent]);

  const fetchGroupEventHeadcounts = useCallback(() => {
    const _fetchGroupEventHeadcounts = async () => {
      try {
        const response = await GroupsService.GetGroupHeadCounts({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['event.id', eventId],
          ],
        });

        if (response.body) {
          setHeadcounts(response.body);
        } else {
          console.warn('Could not fetch headcounts');
          setIsError(true);
        }
      } catch (error) {
        console.error('Error fetching headcounts:', error);
        setIsError(true);
      } finally {
        setIsLoadingHeadCounts(false);
      }
    };

    _fetchGroupEventHeadcounts();
  }, [accessToken, appKey, eventId]);

  const fetchGroupEventAttendances = useCallback(() => {
    const _fetchGroupEventAttendances = async () => {
      try {
        const response = await GroupsService.GetGroupEventAttendances({
          token: accessToken,
          filters: [
            ['app_key', appKey],
            ['event.id', eventId],
          ],
        });

        if (response.body) {
          setGroupEventAttendances(response.body);
        } else {
          debug('Could not fetch group events attendances');
          setIsResponseAttendanceError(true);
        }
      } catch {
      } finally {
        setIsLoadingGroupEventAttendances(false);
      }
    };

    _fetchGroupEventAttendances();
  }, [accessToken, appKey, eventId]);

  useEffect(() => {
    fetchGroupEventAttendances();
  }, [fetchGroupEventAttendances]);

  useEffect(() => {
    fetchGroupEventHeadcounts();
  }, [fetchGroupEventHeadcounts]);

  return (
    <>
      {isLoading || isLoadingHeadCounts || isLoadingGroupEventAttendances ? (
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: Colors.N0,
          }}
        >
          <KitLoader />
        </View>
      ) : isResponseError ? (
        <KitNotFound />
      ) : (
        <ScrollView style={{ backgroundColor: 'white' }}>
          <View
            style={[
              {
                maxWidth: '100%',
                marginTop: Platform.OS !== 'web' && isLandscape ? 14 : 0,
                borderTopWidth: Platform.OS !== 'web' && isLandscape ? 1 : 0,
                borderTopColor:
                  Platform.OS !== 'web' && isLandscape
                    ? Colors.N100
                    : Colors.N0,
              },
              styles.safeContainer,
            ]}
          >
            {showTakeAttendanceComponent ? (
              <EventAttendanceBarComponent
                isAttendanceTaken={isAttendanceTaken}
                attendedCount={headcounts?._embedded?.headcounts?.[0]?.count}
                onPressAttendance={(mode) => {
                  setAttendanceMode(mode);
                  handleAttendancePress(mode);
                  setAttendanceModalVisible(true);
                }}
              />
            ) : null}

            <View
              style={{
                paddingHorizontal: isLarge ? Spacing.xxxxl + 8 : Spacing.l,
                paddingTop: Platform.OS !== 'web' ? Spacing.m : Spacing.xl,
                paddingBottom: groupMembersLessThanFifty
                  ? Spacing.xs
                  : Spacing.xl,
              }}
            >
              {/* Event Header */}
              {groupEvent ? (
                <EventDetailHeader
                  event={groupEvent}
                  eventLocation={eventLocation}
                />
              ) : null}
              {/* Event Location */}
              <EventDetailLocation
                eventLocation={eventLocation}
                // @ts-ignore
                marker={eventLocation?.title}
              />

              {/* Event Description */}
              {groupEvent?.description !== undefined &&
              groupEvent?.description !== '' ? (
                <View
                  style={{
                    position: 'relative',
                    marginTop: Spacing.xxl,
                  }}
                >
                  <KitText
                    fontSize={20}
                    bold
                    color={Colors.N900}
                    style={{ marginBottom: Spacing.xs }}
                  >
                    {t('eventsAbout')}
                  </KitText>
                  <KitText expandable numberOfLines={3}>
                    {groupEvent.description}
                  </KitText>
                </View>
              ) : null}

              {showTakeAttendanceComponent ? (
                <EventAttendanceComponent
                  isAttendanceTaken={isAttendanceTaken}
                  attendedCount={headcounts?._embedded?.headcounts?.[0]?.count}
                  onPressAttendance={handleAttendancePress}
                  groupMembersLessThanFifty={groupMembersLessThanFifty}
                />
              ) : null}
            </View>
            {groupMembersLessThanFifty && isAttendanceTaken ? (
              <View
                style={{
                  paddingHorizontal: isLarge ? Spacing.xxxxl + 8 : 0,
                  paddingBottom: Spacing.xl,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-evenly',
                    marginBottom: Spacing.m,
                  }}
                >
                  <KitTab
                    onPress={() => setActiveTab(0)}
                    active={activeTab === 0}
                    fontSize={14}
                  >
                    {`${t('groups:attended')} (${attendedMembers.length})`}
                  </KitTab>
                  <KitTab
                    onPress={() => setActiveTab(1)}
                    active={activeTab === 1}
                    fontSize={14}
                  >
                    {`${t('groups:missed')} (${notAttendedMembers.length})`}
                  </KitTab>
                </View>

                <KitList
                  data={activeTab === 0 ? attendedMembers : notAttendedMembers}
                  renderItem={({ item, index }) => {
                    const nickname = `${item.nickname}${
                      item.userId === currentId ? ' (you)' : ''
                    }`;
                    const profileImageURL =
                      item.profileUrl ||
                      item._embedded?.['chat-user']?.profile_url;
                    const memberIsManager = isManagerMember(item);

                    return (
                      <KitListItem
                        ImageComponent={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}
                          >
                            <KitAvatar
                              style={{ marginLeft: Spacing.xs }}
                              nickname={nickname}
                              size={Spacing.xxl}
                              imageUrl={profileImageURL}
                            />
                          </View>
                        }
                        innerPaddingHorizontal={Spacing.l}
                        minHeight={79}
                        title={item.nickname}
                        titleFontSize={16}
                        rightElement={
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            {memberIsManager ? (
                              <KitBadge
                                text={t('groups:labelGroupManager')}
                                size='m'
                              />
                            ) : null}
                          </View>
                        }
                        rightIcon=''
                      />
                    );
                  }}
                />
              </View>
            ) : null}
          </View>
        </ScrollView>
      )}
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />

      {Platform.OS === 'web' ? (
        <KitModal
          isVisible={isAttendanceModalVisible}
          onClose={() => setAttendanceModalVisible(false)}
          anchorBottom={isSmall}
          modalStyle={{
            width: '100%',
            maxWidth: 620,
            maxHeight: 600,
            padding: 0,
          }}
          coverScreen={true}
          enableScroll
          noScrollView
        >
          <CreateOrUpdateGroupAttendance
            route={route}
            mode={attendanceMode}
            onCancel={() => setAttendanceModalVisible(false)}
            onSuccess={() => {
              fetchGroupEventHeadcounts();
              fetchGroupEventAttendances();
            }}
            headcounts={headcounts}
            attendances={groupEventAttendances}
            groupEvent={groupEvent}
          />
        </KitModal>
      ) : null}

      <KitModal
        modalStyle={{
          width: '100%',
          maxWidth: 620,
          maxHeight: 700,
          padding: 0,
        }}
        coverScreen={true}
        anchorBottom={isSmall}
        isVisible={openEditGroupEventModal}
        onClose={() => setIsOpenEditGroupEventModal(false)}
        enableScroll
        noScrollView
      >
        <CreateEditGroupEventScreen
          groupEventInWebPlatform={
            repeatingGroupEvent ? repeatingGroupEvent : groupEvent
          }
          onCancel={() => setIsOpenEditGroupEventModal(false)}
        />
      </KitModal>

      {Platform.OS !== 'web' ? (
        <>
          {/* More Action Modal */}
          <MoreActionModal
            isVisible={moreActionModal}
            isManager={isManager}
            onClose={closeMoreActionModal}
            onEditEvent={_onEditEvent}
            onDeleteEvent={_onDeleteEvent}
            onShareEvent={_onShareEvent}
          />
        </>
      ) : null}

      <DeleteEventConfirmModal
        isVisible={eventToDelete}
        onClose={() => setEventToDelete(false)}
        title={
          // @ts-ignore
          groupEvent?.repetition_rules !== undefined
            ? t('deleteRecurringEvent')
            : t('deleteEvent')
        }
        subtitle={t('groups:deleteRecurringEventDesc')}
        confirmAction={_confirmDeleteEvent}
        event={groupEvent ?? {}}
        repeatEvent={repeatEvent ?? {}}
        selectedOption={selectedDeleteOption}
        onOptionSelect={setSelectedDeleteOption}
      />

      <ShareEventConfirmModal
        isVisible={eventToShare}
        onClose={() => setEventToShare(false)}
        title={t('common:shareToChat')}
        subtitle={t('common:shareThisEventDescription')}
        confirmAction={_confirmShareEvent}
        event={groupEvent ?? {}}
      />
    </>
  );
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  viewContainer: {
    flex: 1,
    paddingHorizontal: Spacing.l,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: colorForScheme({ default: Colors.N0 }),
  },
});
